import type { ConfigurableListColumnsController } from '@/hooks/useConfigurableListColumns'

type ListColumnsMenuProps<TId extends string = string> = {
  columns: ConfigurableListColumnsController<TId>
  testIdPrefix?: string
}

/**
 * Reimbursements / resident-style Columns picker (show/hide + drag reorder).
 */
export function ListColumnsMenu<TId extends string>({
  columns,
  testIdPrefix = 'list',
}: ListColumnsMenuProps<TId>) {
  const {
    isColumnsOpen,
    setIsColumnsOpen,
    columnSearchTerm,
    setColumnSearchTerm,
    columnsMenuRef,
    columnsListRef,
    availableColumnOptions,
    filteredColumnOptions,
    selectedAvailableColumnCount,
    areAllAvailableColumnsSelected,
    areNoAvailableColumnsSelected,
    visibleColumnSet,
    draggedColumnId,
    setDraggedColumnId,
    setDragPreviewColumnOrderIds,
    setVisibleColumns,
    toggleColumn,
    handleColumnDragStart,
    handleColumnDragOver,
    handleColumnDrop,
    autoScrollColumnsList,
    resetColumnsToDefault,
    defaultCustomizableColumnIds,
    lockedColumnIds,
  } = columns

  return (
    <div
      ref={columnsMenuRef}
      className="relative z-[5] min-w-0 flex-1 md:flex-none md:shrink-0"
      data-testid={`${testIdPrefix}-columns-menu`}
    >
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation()
          setIsColumnsOpen((isOpen) => !isOpen)
        }}
        data-testid={`${testIdPrefix}-columns-open-button`}
        className="relative inline-flex h-[42px] flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-xl border border-[#1597ff] bg-white px-4 text-sm font-semibold text-[#1597ff] shadow-sm transition-all hover:bg-blue-50 hover:shadow-md md:flex-none"
        aria-expanded={isColumnsOpen}
        aria-haspopup="menu"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <rect width="18" height="18" x="3" y="3" rx="2" />
          <path d="M9 3v18" />
          <path d="M15 3v18" />
        </svg>
        Columns
        <span
          className="text-xs font-bold text-[rgb(21_151_255)]"
          data-testid={`${testIdPrefix}-columns-selected-count`}
        >
          {selectedAvailableColumnCount}/{availableColumnOptions.length}
        </span>
      </button>

      {isColumnsOpen ? (
        <div
          className="absolute right-0 top-[calc(100%+0.5rem)] z-50 w-[min(24rem,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl"
          data-testid={`${testIdPrefix}-columns-dropdown`}
          role="menu"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="border-b border-slate-100 p-4">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-bold text-slate-900">Customize columns</p>
                <p className="mt-1 text-xs leading-5 text-slate-500">
                  Select columns and drag the handle to reorder.
                </p>
              </div>
              <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-bold text-blue-600">
                {selectedAvailableColumnCount}/{availableColumnOptions.length}
              </span>
            </div>
            <div className="relative">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
                aria-hidden
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
              </svg>
              <input
                type="search"
                value={columnSearchTerm}
                onChange={(event) => setColumnSearchTerm(event.target.value)}
                placeholder="Search columns..."
                className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 pl-10 pr-3 text-sm font-medium text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-blue-300 focus:bg-white focus:ring-4 focus:ring-blue-50"
                data-testid={`${testIdPrefix}-columns-search-input`}
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2 border-b border-slate-100 bg-slate-50/60 px-4 py-3">
            <button
              type="button"
              className={`rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-600 transition ${
                areAllAvailableColumnsSelected
                  ? 'cursor-not-allowed opacity-50'
                  : 'hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700'
              }`}
              disabled={areAllAvailableColumnsSelected}
              onClick={() => setVisibleColumns(defaultCustomizableColumnIds)}
              data-testid={`${testIdPrefix}-columns-select-all-button`}
            >
              Select All
            </button>
            <button
              type="button"
              className={`rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-600 transition ${
                areNoAvailableColumnsSelected
                  ? 'cursor-not-allowed opacity-50'
                  : 'hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700'
              }`}
              disabled={areNoAvailableColumnsSelected}
              onClick={() => setVisibleColumns([...lockedColumnIds])}
              data-testid={`${testIdPrefix}-columns-clear-all-button`}
            >
              Clear All
            </button>
            <button
              type="button"
              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-600 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
              onClick={resetColumnsToDefault}
              data-testid={`${testIdPrefix}-columns-reset-button`}
            >
              Reset
            </button>
          </div>

          <div
            ref={columnsListRef}
            className="max-h-72 space-y-1 overflow-y-auto p-3"
            onDragOver={(event) => {
              event.preventDefault()
              autoScrollColumnsList(event.clientY)
            }}
            onDrop={handleColumnDrop}
          >
            {filteredColumnOptions.length > 0 ? (
              filteredColumnOptions.map((column) => (
                <div
                  key={column.id}
                  className={`group flex items-center gap-3 rounded-xl border border-transparent px-3 py-2.5 text-sm font-semibold text-slate-700 transition ${
                    draggedColumnId === column.id
                      ? 'scale-[0.99] border-blue-100 bg-blue-50 opacity-80 shadow-inner'
                      : 'hover:border-slate-100 hover:bg-slate-50'
                  }`}
                  data-column-option-row
                  data-testid={`${testIdPrefix}-columns-option-${column.id}`}
                  onDragOver={(event) => handleColumnDragOver(event, column.id)}
                  onDrop={handleColumnDrop}
                >
                  <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-3">
                    <input
                      type="checkbox"
                      checked={visibleColumnSet.has(column.id)}
                      onChange={() => toggleColumn(column.id)}
                      className="h-4 w-4 shrink-0 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="truncate leading-5">{column.label}</span>
                  </label>
                  <button
                    type="button"
                    draggable
                    className="ml-auto inline-flex h-8 w-8 shrink-0 cursor-grab items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-400 shadow-sm transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700 active:cursor-grabbing group-hover:border-slate-300"
                    onDragStart={(event) => handleColumnDragStart(event, column.id)}
                    onDragEnd={() => {
                      setDraggedColumnId(null)
                      setDragPreviewColumnOrderIds(null)
                    }}
                    aria-label={`Drag ${column.label} to reorder`}
                    title="Drag to reorder"
                    data-testid={`${testIdPrefix}-columns-drag-handle-${column.id}`}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden
                    >
                      <circle cx="9" cy="5" r="1" />
                      <circle cx="9" cy="12" r="1" />
                      <circle cx="9" cy="19" r="1" />
                      <circle cx="15" cy="5" r="1" />
                      <circle cx="15" cy="12" r="1" />
                      <circle cx="15" cy="19" r="1" />
                    </svg>
                  </button>
                </div>
              ))
            ) : (
              <div className="px-3 py-8 text-center text-sm text-slate-500">
                No columns match your search.
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  )
}
